/*
Origin Core 1.0 — Alex Monroe

called from /snippets/variant-main-picker.liquid

SUMMARY
--------------------
This script defines a custom web component <modal-availability> that makes the per-option
stock status inside the Select Size (RingSizeDialog) and Second Option
(SecondProductOptionDialog) modals reflect the WEBSITE location's stock rather than the
all-locations `variant.inventory_quantity` aggregate Liquid renders.

Because the modal shows every option at once, it batch-fetches website stock for all the
product's purchasable option variants from the inventory App Proxy when the modal first
opens:

  GET {proxyUrl}?variant_ids=<csv>  ->  { "websiteInStockByVariant": { "<id>": true|false } }

Per option:
  websiteInStock === true  -> "In stock"          (tickmark)
  websiteInStock === false -> the policy-derived fallback the server rendered with
                              `ignore_inventory: true` — "Made to order" (short, NO lead
                              time in the modal) or "Out of stock".

While the request is in flight each option shows a neutral "Checking availability…" state.
If the request fails or omits a variant, show a neutral unavailable message. Never
infer website availability from boutique stock. Without a proxy, retain server labels.

The component is generic over both modals via `data-prefix` ('ring-size' | 'second-option'),
which selects the matching selected-summary hooks (`data-{prefix}-status-*`). Per-option
card hooks use the generic `data-option-status-*` / `data-option-*` names so they never
collide with the summary hooks driven by variant-picker.js.
*/

import { rememberWebsiteStock } from './website-stock.js';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const OOS_PILL_CLASSES = ['w-fit', 'rounded-full', 'bg-[#FFF4F4]', 'px-4', 'py-2', 'text-black'];

class ModalAvailability extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;
  /** @type {Object<string, boolean> | null} */
  #stockMap = null;
  /** @type {string} key (sorted variant ids) the current #stockMap was resolved for */
  #resolvedKey = '';
  #resolvedAt = 0;
  /** @type {Element | null} */
  #dialogComponent = null;
  /** @type {Element | null} */
  #section = null;
  /** @type {((event: Event) => void) | undefined} */
  #openHandler;
  /** @type {((event: Event) => void) | undefined} */
  #selectedHandler;
  /** @type {((event: Event) => void) | undefined} */
  #updateHandler;

  connectedCallback() {
    super.connectedCallback();

    this.#dialogComponent = this.querySelector('dialog-component');
    if (this.#dialogComponent) {
      // dialog:open is dispatched on the dialog-component and does not bubble.
      this.#openHandler = () => this.#onOpen();
      this.#dialogComponent.addEventListener('dialog:open', this.#openHandler);
    }

    this.#section = this.closest('.shopify-section, dialog');
    if (this.#section) {
      // variant:selected fires synchronously after variant-picker.js #syncRingSizeUi, so
      // our summary update wins without fighting it.
      this.#selectedHandler = () => this.#updateSummary();
      this.#section.addEventListener(ThemeEvents.variantSelected, this.#selectedHandler);

      // Reapply website stock after section rendering; refetch only if the option set changes.
      this.#updateHandler = () => {
        this.#onOpen();
      };
      this.#section.addEventListener(ThemeEvents.variantUpdate, this.#updateHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#dialogComponent && this.#openHandler) {
      this.#dialogComponent.removeEventListener('dialog:open', this.#openHandler);
    }
    if (this.#section) {
      if (this.#selectedHandler) this.#section.removeEventListener(ThemeEvents.variantSelected, this.#selectedHandler);
      if (this.#updateHandler) this.#section.removeEventListener(ThemeEvents.variantUpdate, this.#updateHandler);
    }
    this.#activeFetch?.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#onOpen();
  }

  /** @returns {HTMLInputElement[]} purchasable option inputs (skip disabled/sold-out). */
  #purchasableInputs() {
    return Array.from(this.querySelectorAll('input[data-variant-id][data-option-available="true"]'));
  }

  #onOpen() {
    const proxyUrl = this.dataset.proxyUrl;
    if (!proxyUrl) return; // no proxy configured — leave server labels untouched

    const inputs = this.#purchasableInputs();
    if (!inputs.length) return;

    const ids = inputs.map((i) => i.dataset.variantId).filter(Boolean);
    const key = [...ids].sort().join(',');

    // Already resolved for this exact variant set — just re-apply (covers re-open).
    if (this.#stockMap && this.#resolvedKey === key && Date.now() - this.#resolvedAt < 30_000) {
      this.#applyAll();
      return;
    }

    this.#stockMap = null;
    this.#resolvedKey = '';

    // Neutral checking state for every purchasable option + the summary.
    for (const input of inputs) this.#applyCard(input, { text: this.dataset.checkingText || '', icon: '', type: 'checking' });
    this.#applySummaryState({ text: this.dataset.checkingText || '', icon: '', type: 'checking' });

    const abortController = this.#createAbortController();
    const url = new URL(proxyUrl, window.location.origin);
    url.searchParams.set('variant_ids', ids.join(','));

    fetch(url.toString(), { signal: AbortSignal.any([abortController.signal, AbortSignal.timeout(5000)]) })
      .then((response) => {
        if (!response.ok) throw new Error(`Proxy returned ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (abortController.signal.aborted) return;
        const map = data && data.websiteInStockByVariant;
        if (!map || typeof map !== 'object') {
          this.#applyUnavailable();
          return;
        }
        this.#stockMap = map;
        this.#resolvedKey = key;
        this.#resolvedAt = Date.now();
        for (const [id, value] of Object.entries(map)) rememberWebsiteStock(proxyUrl, id, value);
        this.#applyAll();
      })
      .catch((_error) => {
        if (abortController.signal.aborted) return;
        this.#applyUnavailable();
      });
  }

  #applyAll() {
    for (const input of this.#purchasableInputs()) {
      this.#applyCard(input, this.#resolveState(input));
    }
    this.#updateSummary();
  }

  #applyUnavailable() {
    this.#stockMap = {};
    this.#applyAll();
  }

  /** Updates the selected-summary to match the currently checked option. */
  #updateSummary() {
    const checked = this.querySelector('input[data-variant-id]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    let state;
    if (checked.dataset.optionAvailable !== 'true') {
      state = this.#serverState(checked); // out of stock — server already correct
    } else if (this.#stockMap) {
      state = this.#resolveState(checked);
    } else if (this.dataset.proxyUrl) {
      state = { text: this.dataset.checkingText || '', icon: '', type: 'checking' };
    } else {
      state = this.#serverState(checked);
    }
    this.#applySummaryState({ ...state, text: state.detail || state.text });
  }

  /**
   * Resolves the website-aware state for an option input from the fetched map.
   * @param {HTMLInputElement} input
   */
  #resolveState(input) {
    const id = input.dataset.variantId || '';
    const inStock = this.#stockMap ? this.#stockMap[id] : undefined;
    if (inStock === true) {
      return { text: this.dataset.inStockText || '', detail: this.dataset.inStockDetail || '', icon: 'tickmark', type: 'in_stock' };
    }
    if (inStock !== false) {
      return { text: this.dataset.unavailableText || '', icon: '', type: 'unknown' };
    }
    return {
      detail: input.dataset.optionFallbackDetail || '',
      text: input.dataset.optionFallbackText || '',
      icon: input.dataset.optionFallbackIcon || '',
      type: input.dataset.optionFallbackType || 'out_of_stock',
    };
  }

  /** @param {HTMLInputElement} input */
  #serverState(input) {
    return {
      text: input.dataset.optionServerText || '',
      icon: input.dataset.optionServerIcon || '',
      type: input.dataset.optionServerType || 'in_stock',
    };
  }

  /**
   * Applies a state to one option card (generic data-option-status-* hooks).
   * @param {HTMLInputElement} input
   * @param {{ text: string, icon: string, type: string }} state
   */
  #applyCard(input, state) {
    // Keep the picker’s synchronous selection update on the website-aware state too.
    const key = this.dataset.prefix === 'ring-size' ? 'ringSize' : 'secondOption';
    input.dataset[`${key}StatusType`] = state.type;
    input.dataset[`${key}StatusText`] = state.type === 'made_to_order' ? (state.detail || state.text) : state.text;
    input.dataset[`${key}StatusDetail`] = state.detail || state.text;
    input.dataset[`${key}StatusIcon`] = state.icon;
    const card = input.parentElement;
    if (!card) return;
    const textEl = card.querySelector('[data-option-status-text]');
    if (textEl instanceof HTMLElement) textEl.textContent = state.text;
    const iconWrap = card.querySelector('[data-option-status-icon]');
    if (iconWrap instanceof HTMLElement) {
      iconWrap.classList.toggle('hidden', state.icon === '');
      this.#toggleIcons(iconWrap, state.icon);
    }
  }

  /**
   * Applies a state to the selected-summary (prefix-specific hooks driven elsewhere by
   * variant-picker.js — we override after it).
   * @param {{ text: string, icon: string, type: string }} state
   */
  #applySummaryState(state) {
    const prefix = this.dataset.prefix;
    // Option inputs carry these attributes as data too and precede the footer.
    // Target the visible spans so querySelector cannot select an input instead.
    const detailEl = this.querySelector(`span[data-${prefix}-status-detail]`);
    if (detailEl instanceof HTMLElement) detailEl.textContent = state.text;

    const iconWrap = this.querySelector(`span[data-${prefix}-status-icon]`);
    if (iconWrap instanceof HTMLElement) this.#toggleIcons(iconWrap, state.icon);

    if (prefix === 'second-option') {
      const pill = this.querySelector('[data-second-option-status-pill]');
      if (pill instanceof HTMLElement) {
        const isOos = state.type === 'out_of_stock';
        pill.classList.toggle('justify-center', !isOos);
        for (const cls of OOS_PILL_CLASSES) pill.classList.toggle(cls, isOos);
      }
    }
  }

  /**
   * Shows the matching pre-rendered icon (or none when icon === '').
   * @param {HTMLElement} iconWrap
   * @param {string} icon
   */
  #toggleIcons(iconWrap, icon) {
    iconWrap.querySelectorAll('[data-status-icon-option]').forEach((opt) => {
      const match = opt instanceof HTMLElement && opt.dataset.statusIconOption === icon && icon !== '';
      opt.classList.toggle('hidden', !match);
      opt.classList.toggle('inline-flex', match);
    });
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }
}

if (!customElements.get('modal-availability')) {
  customElements.define('modal-availability', ModalAvailability);
}
